import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'
import { Button } from 'designSystem/components/Button'
import { Input as DsInput } from 'designSystem/components/Input'
import { Textarea } from 'designSystem/components/Textarea'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { generateDocumentId } from 'features/files/utils/fileUploadUtils'
import { piTagsBySlot } from 'features/fim/assetInventory'
import { useRolodexSignedURLs } from 'features/rolodex'
import {
  ContextBundleView,
  EligibilityNotice,
} from 'features/sentinelDesign/ContextBundleView'
import {
  Callout,
  ErrorText,
  ProgressLog,
} from 'features/sentinelDesign/designStyles'
import { EvaluationPanel } from 'features/sentinelDesign/EvaluationPanel'
import { PlanEditor } from 'features/sentinelDesign/PlanEditor'
import {
  isPlanRunnable,
  reconcilePlanWithContext,
} from 'features/sentinelDesign/planEdits'
import { PlanSummary } from 'features/sentinelDesign/PlanSummary'
import { RunFailurePanel } from 'features/sentinelDesign/RunFailurePanel'
import { StageRail } from 'features/sentinelDesign/StageRail'
import {
  defaultStage,
  deriveStageStatuses,
} from 'features/sentinelDesign/stages'
import type { SentinelDesignContext } from 'features/sentinelDesign/types'
import {
  useDesignContext,
  useDurableDesignArtifacts,
  useSentinelEntry,
} from 'features/sentinelDesign/useDurableDesign'
import { SentinelInstancePicker } from 'features/sentinels/SentinelInstancePicker'
import { useAssetInstances } from 'features/sentinels/useAssetInstances'
import {
  findRunOfPlan,
  useSentinelList,
} from 'features/sentinels/useSentinelList'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { useSentinelStudio } from 'features/timeSeries/sentinelStudio/SentinelStudioContext'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry, RolodexEntryType } from 'types'

const Wrap = styled.div`
  padding: 1rem 1.25rem;
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  > * {
    flex-shrink: 0;
  }
`

const H = styled.h2`
  font-size: 0.95rem;
  margin: 0;
`

const Hint = styled.p`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 44rem;
  margin: 0;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Label = styled.label`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/** A checkbox and its label on one line; the global input reset otherwise
 * stretches the checkbox to full width and shoves the label off-screen. */
const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;

  input {
    width: auto;
    margin: 0;
    flex: none;
  }
`

const Input = styled(DsInput)`
  max-width: 28rem;
`

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`

const Stage = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: 0;
`

/** Grid resolution the context bundle and the plan are both built on. */
const DEFAULT_INTERVAL_MINUTES = 10

/** "4 minutes ago", for a run whose live progress this tab never saw. */
const sinceWhen = (iso: string | undefined): string => {
  if (!iso) return t`a little while ago`
  const startedAt = Date.parse(iso)
  if (Number.isNaN(startedAt)) return t`a little while ago`
  const minutes = Math.max(0, Math.round((Date.now() - startedAt) / 60_000))
  if (minutes < 1) return t`just now`
  if (minutes < 60)
    return t`${minutes} minute${{ value136_49: minutes === 1 ? '' : 's' }} ago`
  const hours = Math.round(minutes / 60)
  return t`${hours} hour${{ value138_27: hours === 1 ? '' : 's' }} ago`
}

/**
 * The staged agentic builder inside the customer Sentinels tab: context bundle
 * → designed plan → edits → fit → evaluation.
 *
 * Adapted from the Model Ops `SentinelDesignFlow`, with three differences that
 * make it a chart-coupled, durable tab rather than a page panel:
 *  - state lives in {@link useSentinelStudio} (survives tab switches), not local
 *    `useState`/`useReducer`;
 *  - the training window is the chart's own date range (no in-panel date
 *    picker) — "the date picker is the page's";
 *  - "created" selects the finished run in this tab instead of navigating.
 *
 * The paste source is the working entry point here; the asset-template entry is
 * layered on in a later step. The design engine itself is reused unchanged.
 */
export const SentinelBuilderPanel: FC = () => {
  const { t } = useLingui()

  const {
    timeRange,
    siteID: siteId,
    setSelectedSensors,
    sensorEntries,
    addSentinel,
    activeSentinels,
  } = useScatterplotContext()
  const {
    api,
    plan,
    dispatchPlan: dispatch,
    source,
    setSource,
    sentinelName,
    setSentinelName,
    notes,
    setNotes,
    fullyAgentic,
    setFullyAgentic,
    openStage,
    setOpenStage,
    setSelectedEntryId,
    resumeContextId,
    setResumeContextId,
    selectedTemplateSlug,
    setSelectedTemplateSlug,
    markEngaged,
  } = useSentinelStudio()

  // Pens this builder put on the chart (from the resumed/built context, a
  // pasted tag list, or a picked asset). We remove exactly these when the
  // builder unmounts — clicking out of a design context should not leave its
  // tags pinned to the chart. Tracked in a ref so a manual pen the user added
  // themselves is never swept up.
  const builderPensRef = useRef<string[]>([])
  const trackBuilderPens = (ids: string[]) => {
    for (const id of ids) {
      if (!builderPensRef.current.includes(id)) builderPensRef.current.push(id)
    }
  }
  // Latest active-sentinel targets, read at unmount: a drawn band anchors on its
  // target pen (addSentinel adds it), so the cleanup must never pull a pen a
  // live band still needs.
  const activeTargetsRef = useRef<string[]>([])
  activeTargetsRef.current = activeSentinels.map((sentinel) => sentinel.target)
  useEffect(
    () => () => {
      const ids = builderPensRef.current.filter(
        (id) => !activeTargetsRef.current.includes(id)
      )
      if (ids.length) {
        setSelectedSensors((prev) =>
          prev.filter((pen) => !ids.includes(pen.id))
        )
      }
    },
    // Unmount-only: setSelectedSensors is stable, so this cleanup runs when the
    // builder closes, not on every render.
    [setSelectedSensors]
  )

  // Asset source: filled instances of the site's templates. Selecting one lets
  // the backend resolve its bound tags into the context (no paste needed).
  const { assets, isLoading: assetsLoading } = useAssetInstances(siteId)
  const fillable = useMemo(
    () => assets.filter((asset) => asset.totalCount > 0),
    [assets]
  )
  const selectedAsset = fillable.find(
    (asset) => asset.slug === selectedTemplateSlug
  )
  const selectAsset = (slug: string) => {
    setSelectedTemplateSlug(slug)
    api.resetContext()
    setResumeContextId(null)
    dispatch({ type: 'clear' })
    setOpenStage(null)
    // Populate the graph with the asset's bound tags right away — the agent
    // refines target/drivers when the context builds, but you see the asset's
    // data on the chart the moment you pick it.
    const asset = fillable.find((a) => a.slug === slug)
    const tags = asset
      ? Object.values(piTagsBySlot(asset.instance.fill)).flat()
      : []
    if (!tags.length) return
    const entries = tags
      .map((tag) =>
        sensorEntries.find(
          (sensor) =>
            sensor.id === tag ||
            (sensor.data as { pi_tag?: string }).pi_tag === tag
        )
      )
      .filter((entry): entry is BaseSensorEntry => entry !== undefined)
    if (!entries.length) return
    trackBuilderPens(entries.map((entry) => entry.id))
    setSelectedSensors((prev) => {
      const missing = entries.filter(
        (entry) => !prev.some((p) => p.id === entry.id)
      )
      return missing.length ? [...prev, ...missing] : prev
    })
  }

  // Opening the builder engages the session (mirrors the Experiment Lab).
  useEffect(() => {
    markEngaged()
  }, [markEngaged])

  // Milestone: as the agentic context resolves, put the tags it chose onto the
  // chart as pens, so you watch the model take shape on the data it's using.
  useEffect(() => {
    const tags = api.context?.tags
    if (!tags?.length) return
    const ids = tags
      .map((tag) => tag.datapoint_id)
      .filter((id): id is string => !!id)
    if (!ids.length) return
    const entries = ids
      .map((id) => sensorEntries.find((sensor) => sensor.id === id))
      .filter((entry): entry is BaseSensorEntry => entry !== undefined)
    if (!entries.length) return
    trackBuilderPens(entries.map((entry) => entry.id))
    setSelectedSensors((prev) => {
      const missing = entries.filter(
        (entry) => !prev.some((sensor) => sensor.id === entry.id)
      )
      return missing.length ? [...prev, ...missing] : prev
    })
  }, [api.context, setSelectedSensors, sensorEntries])

  // The training window is the chart's visible range; the page's own picker
  // (the footer range selector) is the only date control.
  const startDate = dayjs(timeRange.from).format('YYYY-MM-DD')
  const endDate = dayjs(timeRange.to).format('YYYY-MM-DD')

  const [pastedTags, setPastedTags] = useState('')
  const parsedTags = useMemo(
    () =>
      Array.from(
        new Set(
          pastedTags
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
        )
      ),
    [pastedTags]
  )

  // Paste source: as you type ids, put the ones that resolve to a site tag onto
  // the chart as pens right away — the same "see the data as you pick it" the
  // asset picker gives, before the context builds.
  useEffect(() => {
    if (source !== 'paste' || !parsedTags.length) return
    const entries = parsedTags
      .map((tag) =>
        sensorEntries.find(
          (sensor) =>
            sensor.id === tag ||
            (sensor.data as { pi_tag?: string }).pi_tag === tag
        )
      )
      .filter((entry): entry is BaseSensorEntry => entry !== undefined)
    if (!entries.length) return
    trackBuilderPens(entries.map((entry) => entry.id))
    setSelectedSensors((prev) => {
      const missing = entries.filter(
        (entry) => !prev.some((p) => p.id === entry.id)
      )
      return missing.length ? [...prev, ...missing] : prev
    })
  }, [source, parsedTags, setSelectedSensors, sensorEntries])

  const { rolodexPut, rolodexUploadFile } = useRolodexEdit()
  const [pastePid, setPastePid] = useState<{
    entryId: string
    path: string
    name: string
  } | null>(null)
  const [pastePidUploading, setPastePidUploading] = useState(false)
  const [pastePidError, setPastePidError] = useState<string | null>(null)
  const pastePidSigned = useRolodexSignedURLs(
    pastePid ? [{ entry_id: pastePid.entryId, path: pastePid.path }] : undefined
  )
  const pastePidUrl = pastePidSigned.url

  const uploadPastePid = async (file: File) => {
    setPastePidUploading(true)
    setPastePidError(null)
    setPastePid(null)
    try {
      const entryId = generateDocumentId()
      await rolodexPut([
        {
          id: entryId,
          type: RolodexEntryType.REPORT,
          data: {
            type: RolodexEntryType.REPORT,
            entry_id: entryId,
            filename: file.name,
            size: file.size,
            file_type: 'pdf',
            upload_date: new Date().toISOString(),
            visible_name: file.name,
            fields: {},
          },
          tags: { site: siteId },
          index: {},
        },
      ])
      await rolodexUploadFile(entryId, file, file.name)
      setPastePid({ entryId, path: file.name, name: file.name })
    } catch (err) {
      setPastePidError(
        err instanceof Error ? err.message : t`P&ID upload failed`
      )
    } finally {
      setPastePidUploading(false)
    }
  }

  const { adoptContext } = api

  const readyForDetails =
    source === 'paste' ? parsedTags.length >= 3 : !!selectedAsset

  // Resume a context the list reopened (an unfinished design, or a finished
  // run's "continue from"). Adopting it once populates api.context; the fetch
  // key then goes null. A build this tab is streaming reads nothing here.
  const resumed = useDesignContext(
    siteId,
    api.context || api.contextLoading
      ? undefined
      : (resumeContextId ?? undefined)
  )
  useEffect(() => {
    if (resumed.context) adoptContext(resumed.context)
  }, [resumed.context, adoptContext])

  // Park the context id the moment the stream names it, so the list can find
  // this build again and a tab switch can resume it.
  useEffect(() => {
    if (api.contextId && api.contextId !== resumeContextId) {
      setResumeContextId(api.contextId)
    }
  }, [api.contextId, resumeContextId, setResumeContextId])

  const context = api.context
  const eligible = context?.eligibility.ok ?? false
  const contextEntryId = context?.entry_id

  const { entry: contextEntry } = useSentinelEntry(contextEntryId)
  const planRevision = contextEntry?.data.summary?.plan_revision ?? 0
  const durable = useDurableDesignArtifacts(
    contextEntryId,
    contextEntry?.data.files,
    planRevision
  )

  // Seed the editable draft from the persisted plan when this tab has none (the
  // reload/resume case). A draft in hand always wins, so edits are never lost.
  useEffect(() => {
    if (plan === null && durable.plan && context) {
      dispatch({
        type: 'reset',
        plan: reconcilePlanWithContext(durable.plan, context),
      })
    }
  }, [plan, durable.plan, context, dispatch])

  const planReady = plan !== null && isPlanRunnable(plan)
  const design = api.design ?? durable.design
  const evaluation = api.evaluation ?? durable.evaluation

  const runName = sentinelName.trim() || 'sentinel'
  const { sentinels, awaitNewRun } = useSentinelList(siteId)
  const runEntry = api.runEntryId
    ? sentinels.find((sentinel) => sentinel.id === api.runEntryId)
    : findRunOfPlan(sentinels, contextEntryId, plan?.target)
  const runInFlight = runEntry?.runState === 'running'

  useEffect(() => {
    if (api.runEntryId) void awaitNewRun(api.runEntryId)
  }, [api.runEntryId, awaitNewRun])

  // Milestone: once the fit lands, stream its band onto the chart (adds the
  // target pen, enables the sentinels layer, scores the visible window). Guarded
  // so it fires once per run rather than on every poll.
  const bandedRunRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      runEntry?.runState === 'done' &&
      runEntry.target &&
      bandedRunRef.current !== runEntry.id
    ) {
      bandedRunRef.current = runEntry.id
      addSentinel(runEntry.id, runEntry.target, runEntry.name, {
        windowStart: runEntry.windowStart,
        windowEnd: runEntry.windowEnd,
        splitDate: runEntry.splitDate,
      })
    }
  }, [runEntry, addSentinel])

  // "Created" in the tab means: open this run's detail (Overview/Monitor) and
  // collapse the builder back to the list.
  const onCreated = (entryId: string) => {
    setSelectedEntryId(entryId)
    setOpenStage(null)
  }

  const statuses = useMemo(
    () =>
      deriveStageStatuses({
        instanceSelected:
          source === 'paste' ? parsedTags.length >= 3 : !!selectedAsset,
        contextLoading:
          api.contextLoading || resumed.isLoading || !!resumed.building,
        contextError: !!api.contextError || !!resumed.error || resumed.stalled,
        hasContext: !!context,
        contextEligible: eligible,
        contextTagCount: context?.tags.length ?? 0,
        designLoading: api.designLoading,
        designError: !!api.designError,
        hasPlan: !!plan,
        planRunnable: planReady,
        planTarget: plan?.target,
        planDriverCount: plan?.drivers.length ?? 0,
        runState: runEntry?.runState,
        runStarting: api.runLoading,
        runError: !!api.runError,
        runStageLabel: runEntry?.progress?.stage,
        evaluateLoading: api.evaluateLoading,
        evaluateError: !!api.evaluateError,
        hasEvaluation: !!evaluation,
        evaluationVerdict: evaluation?.assessment,
      }),
    [
      source,
      selectedAsset,
      parsedTags,
      api.contextLoading,
      api.contextError,
      api.designLoading,
      api.designError,
      api.runLoading,
      api.runError,
      api.evaluateLoading,
      api.evaluateError,
      resumed.isLoading,
      resumed.building,
      resumed.stalled,
      resumed.error,
      context,
      eligible,
      plan,
      planReady,
      runEntry,
      evaluation,
    ]
  )

  const active = openStage ?? defaultStage(statuses)
  const stageLog = api.progress[active]
  const contextInFlight = api.contextLoading || !!resumed.building
  const evaluatable =
    runEntry?.runState === 'done' ? runEntry.id : api.runEntryId

  /** The fully-agentic chain: design → run → evaluate, off each stage's return
   * (not React state), gated exactly like the manual buttons. */
  const autoRun = async (
    built: SentinelDesignContext,
    name: string
  ): Promise<void> => {
    if (!built.eligibility.ok) return
    const designed = await api.requestDesign(notes)
    if (!designed?.plan) return
    const runnable = reconcilePlanWithContext(designed.plan, built)
    dispatch({ type: 'reset', plan: runnable })
    if (!isPlanRunnable(runnable)) return
    const entryId = await api.startRun(runnable, name)
    if (!entryId) return
    await api.evaluate(entryId)
    onCreated(entryId)
  }

  const buildContext = async () => {
    if (source === 'paste') {
      if (parsedTags.length < 3) return
      const built = await api.buildContext({
        tags: parsedTags,
        pid_signed_url: pastePidUrl ?? null,
        start: startDate,
        end: endDate,
        interval_minutes: DEFAULT_INTERVAL_MINUTES,
      })
      if (!built) return
      const name = sentinelName.trim() || 'sentinel'
      if (!sentinelName.trim()) setSentinelName('sentinel')
      if (fullyAgentic) return autoRun(built, name)
      setOpenStage(null)
      return
    }
    // Asset source: the backend resolves the instance's bound tags. P&ID is
    // optional (the agent plans from tags + stats without it).
    if (!selectedAsset) return
    const built = await api.buildContext({
      instance_entry_id: selectedAsset.slug,
      pid_signed_url: null,
      start: startDate,
      end: endDate,
      interval_minutes: DEFAULT_INTERVAL_MINUTES,
    })
    if (!built) return
    const name = sentinelName.trim() || selectedAsset.name
    if (!sentinelName.trim()) setSentinelName(selectedAsset.name)
    if (fullyAgentic) return autoRun(built, name)
    setOpenStage(null)
  }

  const runDesign = async () => {
    const result = await api.requestDesign(notes)
    if (result && context) {
      dispatch({
        type: 'reset',
        plan: reconcilePlanWithContext(result.plan, context),
      })
      setOpenStage(null)
    }
  }

  const startRun = async () => {
    if (!plan || runInFlight) return
    const entryId = await api.startRun(plan, runName)
    if (entryId) onCreated(entryId)
  }

  return (
    <Wrap>
      <H>
        <Trans>New sentinel</Trans>
      </H>
      <Hint>
        <Trans>
          Paste datapoint ids and the agent assembles a context bundle, designs
          a model plan from it, and you edit that plan before anything is
          fitted. The training window is the chart&apos;s date range (
        </Trans>
        {startDate} → {endDate}
        <Trans>) — change it with the range selector below the chart.</Trans>
      </Hint>

      <StageRail statuses={statuses} active={active} onSelect={setOpenStage} />

      {api.contextError && <ErrorText>{api.contextError}</ErrorText>}
      {resumed.error && <ErrorText>{resumed.error}</ErrorText>}
      {api.designError && <ErrorText>{api.designError}</ErrorText>}
      {api.runError && <ErrorText>{api.runError}</ErrorText>}
      {api.evaluateError && <ErrorText>{api.evaluateError}</ErrorText>}

      {stageLog.length > 0 && <ProgressLog>{stageLog.join('\n')}</ProgressLog>}

      {active === 'context' && (
        <Stage aria-label={t`Context`}>
          {resumed.building && (
            <Callout>
              <strong>
                <Trans>Building the context bundle</Trans>
              </strong>
              {resumed.building.message
                ? ` — ${resumed.building.message}`
                : '.'}{' '}
              <Trans>
                The historian pull and a plot per tag take a few minutes; it
                appears here on its own when it lands.
              </Trans>
            </Callout>
          )}
          {resumed.stalled && (
            <Callout $tone='error'>
              <strong>
                <Trans>This context build didn&apos;t finish.</Trans>
              </strong>
              <Trans>
                {' '}
                It stopped reporting a while ago — its job most likely died.
                Start it again below; nothing was kept.
              </Trans>
            </Callout>
          )}

          <Row role='tablist' aria-label={t`Tag source`}>
            <Button
              variant={source !== 'paste' ? 'primaryRaised' : 'secondary'}
              onClick={() => setSource('instance')}>
              <Trans>From an asset</Trans>
            </Button>
            <Button
              variant={source === 'paste' ? 'primaryRaised' : 'secondary'}
              onClick={() => setSource('paste')}>
              <Trans>Paste tags</Trans>
            </Button>
          </Row>

          {source !== 'paste' && (
            <Field>
              <Label>
                <Trans>Asset</Trans>
              </Label>
              <SentinelInstancePicker
                instances={fillable}
                isLoading={assetsLoading}
                selectedSlug={selectedTemplateSlug ?? ''}
                onSelect={selectAsset}
              />
              <Hint>
                <Trans>
                  Pick a filled asset and the agent builds a monitor from its
                  tags. The training window is the chart&apos;s date range.
                </Trans>
              </Hint>
            </Field>
          )}

          {source === 'paste' && (
            <Field>
              <Label htmlFor='sentinel-tags'>
                <Trans>Datapoint ids, one per line</Trans>
              </Label>
              <Textarea
                id='sentinel-tags'
                value={pastedTags}
                onChange={(event) => setPastedTags(event.target.value)}
                placeholder={t`8f3a-tempA
8f3a-tempB
8f3a-flow01
8f3a-pressure`}
                style={{ maxWidth: '28rem', minHeight: '7rem' }}
              />
              <Hint>
                <Trans>
                  The agent picks the target and drivers from these. At least
                  three are needed.
                </Trans>
              </Hint>
            </Field>
          )}

          {readyForDetails && (
            <>
              <Field>
                <Label htmlFor='sentinel-name'>
                  <Trans>Sentinel name</Trans>
                </Label>
                <Input
                  id='sentinel-name'
                  value={sentinelName}
                  onChange={(event) => setSentinelName(event.target.value)}
                  placeholder='sentinel'
                />
              </Field>

              <Field>
                <Label htmlFor='sentinel-notes'>
                  <Trans>Notes for the design agent</Trans>
                </Label>
                <Input
                  id='sentinel-notes'
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={t`anything the agent should know about this asset`}
                />
              </Field>

              <Field>
                <Label htmlFor='sentinel-pid'>
                  <Trans>P&amp;ID (optional PDF)</Trans>
                </Label>
                <input
                  id='sentinel-pid'
                  type='file'
                  accept='application/pdf'
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) void uploadPastePid(file)
                  }}
                />
                {pastePidUploading && (
                  <Hint>
                    <Trans>Uploading…</Trans>
                  </Hint>
                )}
                {pastePidError && (
                  <Callout $tone='warning'>
                    <Trans>P&amp;ID upload failed: </Trans>
                    {pastePidError}
                    <Trans>. You can retry or build without it.</Trans>
                  </Callout>
                )}
                {pastePid && !pastePidUploading && (
                  <Callout>
                    <strong>
                      <Trans>P&amp;ID to send:</Trans>
                    </strong>{' '}
                    {pastePid.name}
                    {pastePidSigned.isLoading ? t` — signing…` : ''}
                  </Callout>
                )}
                {!pastePid && !pastePidUploading && !pastePidError && (
                  <Hint>
                    <Trans>
                      Without a P&amp;ID the agent plans from the tags and stats
                      alone.
                    </Trans>
                  </Hint>
                )}
              </Field>

              <Field>
                <CheckboxRow htmlFor='sentinel-fully-agentic'>
                  <input
                    id='sentinel-fully-agentic'
                    type='checkbox'
                    checked={fullyAgentic}
                    onChange={(event) => setFullyAgentic(event.target.checked)}
                  />
                  <span>
                    <Trans>
                      Fully agentic — design, run and evaluate automatically
                    </Trans>
                  </span>
                </CheckboxRow>
                <Hint>
                  <Trans>
                    Builds the context, then designs, fits and evaluates end to
                    end without the per-stage buttons. Stops early if the
                    context is ineligible or the plan cannot run.
                  </Trans>
                </Hint>
              </Field>

              <Row>
                <Button
                  variant='primaryRaised'
                  disabled={
                    contextInFlight ||
                    pastePidUploading ||
                    pastePidSigned.isLoading
                  }
                  onClick={() => void buildContext()}>
                  {contextInFlight
                    ? t`Building context…`
                    : fullyAgentic
                      ? t`Build & run everything`
                      : context
                        ? t`Rebuild context`
                        : t`Build context`}
                </Button>
              </Row>
            </>
          )}

          {context && (
            <EligibilityNotice
              eligibility={context.eligibility}
              tagResolution={context.tag_resolution}
            />
          )}
          {context && eligible && (
            <ContextBundleView
              context={context}
              fetchTagPlot={api.fetchTagPlot}
            />
          )}
        </Stage>
      )}

      {active === 'plan' && (
        <Stage aria-label={t`Plan`}>
          <Row>
            <Button
              disabled={!context || !eligible || api.designLoading}
              onClick={() => void runDesign()}>
              {api.designLoading
                ? t`Designing…`
                : plan
                  ? t`Design again`
                  : t`Design the model`}
            </Button>
          </Row>
          {plan && durable.plan && (
            <Hint>
              <Trans>
                Saved on the context bundle, so this plan survives a reload
              </Trans>
              {planRevision > 1
                ? t` (revision ${planRevision} — redesigning replaces it)`
                : ''}
              .
            </Hint>
          )}
          {plan && context && !runInFlight && (
            <PlanEditor
              design={design ?? { plan }}
              plan={plan}
              context={context}
              dispatch={dispatch}
            />
          )}
          {plan && runInFlight && (
            <PlanSummary
              plan={plan}
              rationale={design?.rationale}
              narrative={design?.narrative}
              warnings={design?.warnings}
              frozenBecause={t`A training job is fitting this plan, so it can no longer be edited.`}
            />
          )}
          {plan && !planReady && (
            <Hint>
              <Trans>
                A run needs a target, at least one driver, and at least one
                method.
              </Trans>
            </Hint>
          )}
        </Stage>
      )}

      {active === 'run' && (
        <Stage aria-label={t`Run`}>
          {runInFlight ? (
            <Callout>
              <strong>
                <Trans>Training</Trans>
              </strong>
              <Trans>, started</Trans> {sinceWhen(runEntry?.requestedAt)}
              {runEntry?.progress?.stage ? ` — ${runEntry.progress.stage}` : ''}
              <Trans>
                . It updates on its own; every run gets its own sentinel, so a
                second target off this same context is a new saved model.
              </Trans>
            </Callout>
          ) : (
            <Row>
              <Button
                variant='primaryRaised'
                disabled={!planReady || api.runLoading}
                onClick={() => void startRun()}>
                {api.runLoading ? t`Starting…` : t`Run`}
              </Button>
            </Row>
          )}
          {runEntry?.runState === 'stalled' && (
            <Callout $tone='warning'>
              <Trans>This run started </Trans>
              {sinceWhen(runEntry.requestedAt)}
              <Trans>
                {' '}
                and has neither finished nor failed — its training job was
                likely killed. Delete it and design a new one.
              </Trans>
            </Callout>
          )}
          {runEntry?.runState === 'error' && (
            <RunFailurePanel
              error={runEntry.error}
              failure={runEntry.failure}
              lastStage={runEntry.progress?.stage}
              requestedAt={runEntry.requestedAt}
            />
          )}
          {runEntry?.runState === 'done' && (
            <Callout>
              <strong>
                <Trans>Fitted and saved</Trans>
              </strong>{' '}
              as {runEntry.name}
              <Trans>
                . The plots, metrics and served model are on the sentinel
                itself.
              </Trans>
              <Row>
                <Button
                  variant='primaryRaised'
                  onClick={() => onCreated(runEntry.id)}>
                  <Trans>Open the sentinel</Trans>
                </Button>
              </Row>
            </Callout>
          )}
        </Stage>
      )}

      {active === 'evaluate' && (
        <Stage aria-label={t`Evaluate`}>
          <Row>
            <Button
              disabled={!evaluatable || api.evaluateLoading}
              onClick={() => void api.evaluate(evaluatable ?? undefined)}>
              {api.evaluateLoading ? t`Evaluating…` : t`Evaluate`}
            </Button>
          </Row>
          {!evaluatable && (
            <Hint>
              <Trans>
                Evaluating reads the fitted residuals off the run, so it needs a
                finished one.
              </Trans>
            </Hint>
          )}
          {evaluation && <EvaluationPanel evaluation={evaluation} />}
        </Stage>
      )}

      {plan && active !== 'plan' && (
        <PlanSummary
          plan={plan}
          rationale={design?.rationale}
          narrative={design?.narrative}
          warnings={design?.warnings}
          frozenBecause={t`Open the Plan stage to edit this.`}
        />
      )}
    </Wrap>
  )
}
